import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Minimal manager-facing review list for offline sync conflicts — a sale
 * synced after a connectivity drop where an item had less stock available
 * than the cashier saw offline. Nothing was oversold; this just confirms it.
 * A richer dedicated review screen is planned for Phase 2.
 */
export default function SyncConflictsDialog({ open, onOpenChange, conflicts, onResolved }) {
  const [resolving, setResolving] = useState(null);

  const resolve = async (saleId) => {
    setResolving(saleId);
    try {
      await api.post(`/sync/conflicts/${saleId}/resolve`);
      toast.success('Marked as reviewed');
      onResolved?.(saleId);
    } catch {
      toast.error('Could not mark as reviewed');
    } finally {
      setResolving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sync-conflicts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> Offline sync — quick review
          </DialogTitle>
          <DialogDescription>
            These sales synced after a connectivity drop. The item(s) below had less stock
            available than the cashier saw while offline — nothing oversold, just confirm it's fine.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {(!conflicts || conflicts.length === 0) && (
            <p className="text-sm text-muted-foreground" data-testid="no-sync-conflicts">No open sync issues.</p>
          )}
          {conflicts?.map((c) => (
            <div key={c.sale_id} className="border rounded-lg p-3 text-sm" data-testid={`sync-conflict-${c.sale_id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-medium">Sale {c.sale_id}</span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={resolving === c.sale_id}
                  onClick={() => resolve(c.sale_id)}
                  data-testid={`resolve-conflict-${c.sale_id}`}
                >
                  <Check className="h-3.5 w-3.5 mr-1" /> Mark reviewed
                </Button>
              </div>
              {(c.conflicts || []).map((item, i) => (
                <div key={item.product_id || `${c.sale_id}-${i}`} className="text-muted-foreground">
                  {item.product_name}: requested {item.requested_qty}, only {item.available_qty} available
                </div>
              ))}
            </div>
          ))}
        </div>
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="w-full justify-center gap-1.5 text-xs"
          onClick={() => onOpenChange(false)}
          data-testid="open-full-sync-review-btn"
        >
          <Link to="/sync-conflicts">
            Open full review <ExternalLink className="h-3 w-3" />
          </Link>
        </Button>
      </DialogContent>
    </Dialog>
  );
}
